import { oneconnect } from '../lib/oneconnect';
import hofCookie from '../lib/cookie-data';
import logging from '../lib/logging';

class OneApi {
	#ONE_AT_COOKIE = 'oneconnect.accessToken';
	#ONE_RT_COOKIE = 'oneconnect.refreshToken';
	#ONE_OCID_COOKIE = 'oneconnect.ocid';
	#ONE_ET_COOKIE = 'oneconnect.expireTimestamp';

	constructor() {
		this.logout = this.logout.bind(this);
	}

	/**
	 * @returns {OneConnectOptions}
	 */
	#getAuthenticateOption() {
		const baseUrl = isHofProd
			? 'https://trs-cdn-akm.playtika.com/playtika/oneauth-client/production'
			: 'https://trs-cdn-preprod.playtika.com/playtika/oneauth-client/sandbox';

		return {
			platform: 'web',
			clientId: 'hof',
			baseUrl,
			refreshUrl: isHofProd
				? 'https://one-zuul.playtika.com/one/oauth/token'
				: 'https://one-preprod-zuul.playtika.com/one/oauth/token',
			language: 'en',
		};
	}

	/**
	 * @returns {Promise<boolean>}
	 */
	logout() {
		return new Promise((resolve, reject) => {
			oneconnect.Sdk.logout();
			this.#cleanUpOneConnect();
			resolve(true);
		});
	}

	/**
	 * @returns {Promise<OneConnectAuthData>}
	 */
	logIn() {
		return new Promise((resolve, reject) => {
			try {
				oneconnect.Sdk.setStyle('one-login');
				oneconnect.Sdk.authenticate(this.#getAuthenticateOption())
					.then((result) => {
						this.#populateOneConnect(result);

						resolve(result);
					})
					.catchError((e) => reject(e));
			} catch (e) {
				reject(e);
			}
		});
	}

	#cleanUpOneConnect() {
		[
			this.#ONE_AT_COOKIE,
			this.#ONE_RT_COOKIE,
			this.#ONE_OCID_COOKIE,
			this.#ONE_ET_COOKIE,
		].forEach(hofCookie.remove);
	}

	#oneConnectRefreshToken() {
		return new Promise((resolve, reject) => {
			oneconnect.Sdk.get_silent()
				.refresh(this.#getAuthenticateOption())
				.then((result) => {
					resolve(result);
				})
				.catchError((e) => reject(e));
		});
	}

	/**
	 * @returns {string|null}
	 */
	get #refreshToken() {
		return oneconnect.Sdk.get_storage().get_refreshToken();
	}

	get #isAccessTokenExpired() {
		return oneconnect.Sdk.get_silent().isAccessTokenExpired();
	}

	get #authData() {
		return oneconnect.Sdk.get_silent().createAuthDataOfStoredSession();
	}

	/**
	 * @param {OneConnectAuthData} data
	 */
	#populateStorage(data) {
		oneconnect.Sdk.get_storage().populate(data);
	}

	/**
	 * @param {OneConnectAuthData} data
	 */
	#populateOneConnect(data) {
		hofCookie.set(this.#ONE_AT_COOKIE, data.access_token);
		hofCookie.set(this.#ONE_RT_COOKIE, data.refresh_token);
		hofCookie.set(this.#ONE_OCID_COOKIE, data.ocid);
		hofCookie.set(this.#ONE_ET_COOKIE, data.expires_in);
	}

	/**
	 *
	 * @returns {Promise<OneSessionData|undefined>}
	 */
	async readSessionData() {
		/**
		 * @param {*} data
		 * @returns {object|undefined}
		 */
		const filterObjectData = (data) =>
			typeof data === 'object' && Object.values(data).filter(Boolean).length > 0
				? data
				: undefined;

		/**
		 * @type {OneConnectAuthDataSession|undefined}
		 */
		const currentOneData = filterObjectData(this.#authData);

		if (!currentOneData) {
			/**
			 * For some reason, localStorage is completely cleared externally on the client side.
			 * Therefore, oneconnect stops working. The entire oneconnect API is built around localStorage.
			 * Therefore, as soon as the user logs in, data should be duplicated in cookies
			 *
			 * @type {OneConnectAuthDataSession|undefined}
			 */

			const dataFromWebLoading = filterObjectData({
				access_token: hofCookie.get(this.#ONE_AT_COOKIE),
				refresh_token: hofCookie.get(this.#ONE_RT_COOKIE),
				ocid: hofCookie.get(this.#ONE_OCID_COOKIE),
				expires_in: hofCookie.get(this.#ONE_ET_COOKIE),
			});

			if (dataFromWebLoading) {
				this.#populateStorage(dataFromWebLoading);
			}
		}

		if (!Boolean(this.#refreshToken)) {
			return undefined;
		}

		if (this.#isAccessTokenExpired) {
			try {
				await this.#oneConnectRefreshToken();
			} catch (e) {
				logging('RefreshToken', () => console.log(e), 'danger');

				return undefined;
			}
		}

		if (this.#isAccessTokenExpired) {
			return undefined;
		}

		const oneConnectSession = this.#authData;

		return {
			oneAccessToken: oneConnectSession.access_token,
			oneRefreshToken: oneConnectSession.refresh_token,
			oneExpiresIn: oneConnectSession.expires_in,
		};
	}
}

const oneApi = new OneApi();

export default oneApi;
